"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const rxjs_1 = require("rxjs");
const GraphandModelListPromise_1 = __importDefault(require("./GraphandModelListPromise"));
/**
 * @class GraphandModelList
 */
class GraphandModelList extends Array {
    constructor({ model, count, query, rows }, ...elements) {
        if (!(elements === null || elements === void 0 ? void 0 : elements.length)) {
            elements = [];
        }
        if (rows === null || rows === void 0 ? void 0 : rows.length) {
            elements = elements.concat(rows);
        }
        if (!model) {
            // @ts-ignore
            return super(...elements);
        }
        super(...elements);
        this._model = model;
        this.count = count || 0;
        this._query = query;
        this._subscriptions = new Set();
        Object.defineProperty(this, "_model", { enumerable: false });
        Object.defineProperty(this, "count", { enumerable: false });
        Object.defineProperty(this, "_query", { enumerable: false });
    }
    /**
     * @readonly
     * @type {GraphandModel}
     * @public
     */
    get model() {
        var _a;
        return this._model || ((_a = this[0]) === null || _a === void 0 ? void 0 : _a.constructor);
    }
    /**
     * @readonly
     * @type {any}
     * @public
     */
    get query() {
        return this._query || { ids: this.ids };
    }
    /**
     * @readonly
     * @type {string[]}
     * @public
     */
    get ids() {
        return this.toArray()
            .map((item) => (item === null || item === void 0 ? void 0 : item._id) || String(item))
            .filter(Boolean);
    }
    /**
     * @readonly
     * @type {GraphandModelListPromise}
     * @public
     */
    get promise() {
        return new GraphandModelListPromise_1.default((resolve) => {
            resolve(this);
        }, this.model, this.query);
    }
    /**
     * Hydrate GraphandModelList from serialized data
     * @param data {any} - Serialized data
     * @returns {GraphandModelList}
     */
    static hydrate(data, model) {
        if (!model) {
            throw new Error(`You need to provide a model to hydrate a new GraphandModelList`);
        }
        data = data !== null && data !== void 0 ? data : {};
        if (Array.isArray(data)) {
            const list = data.map((i) => new model(i));
            return new GraphandModelList({ model }, ...list);
        }
        const { __count: count, __query: query, __payload } = data;
        const items = __payload ? __payload.map((i) => model.hydrate(i)) : [];
        return new GraphandModelList({ model, count, query }, ...items);
    }
    toArray() {
        return new Array(...this);
    }
    clone(concatWith) {
        var _a;
        let concatArray;
        if (Array.isArray(concatWith)) {
            concatArray = ((_a = concatWith.toArray) === null || _a === void 0 ? void 0 : _a.call(concatWith)) || concatWith;
        }
        else if (concatWith) {
            concatArray = [concatWith];
        }
        const elements = (concatArray === null || concatArray === void 0 ? void 0 : concatArray.length) ? this.toArray().concat(concatArray) : this.toArray();
        return new GraphandModelList(this, ...elements);
    }
    async reload() {
        const list = await this.model.getList(this.query);
        this.splice(0, this.length, ...list);
        this.count = list.count;
        return this;
    }
    createObservable() {
        this._observable = new rxjs_1.Observable((subscriber) => {
            let prevLastUpdated = this.map((i) => i.updatedAt).sort((a, b) => b.getTime() - a.getTime())[0];
            const _updater = async () => {
                await new Promise((resolve) => setTimeout(resolve));
                const list = await this.model.getList(this.query);
                const lastUpdated = list.map((i) => i.updatedAt).sort((a, b) => b - a)[0];
                if (this.length !== list.length || this.count !== list.count || lastUpdated > prevLastUpdated) {
                    prevLastUpdated = lastUpdated;
                    this.splice(0, this.length, ...list.toArray());
                    this.count = list.count;
                    subscriber.next(this);
                }
            };
            this._storeSub = this.model._listSubject.subscribe(_updater);
        });
    }
    /**
     * Subscribe to the list. The callback will be called each time (an instance inside) the list is updated in store.
     * If the model is synced (realtime), the callback will be called when the list is updated via socket
     * @param callback - The function to call when the list is updated
     */
    subscribe(callback) {
        if (!this._observable) {
            this.createObservable();
        }
        const sub = this._observable.subscribe(callback);
        this._subscriptions.add(sub);
        const unsubscribe = sub.unsubscribe;
        sub.unsubscribe = () => {
            var _a;
            unsubscribe.apply(sub);
            this._subscriptions.delete(sub);
            if (!this._subscriptions.size) {
                (_a = this._storeSub) === null || _a === void 0 ? void 0 : _a.unsubscribe();
                delete this._observable;
            }
        };
        sub.next(this);
        return sub;
    }
    encodeQuery() {
        return this.ids;
    }
    toString() {
        return JSON.stringify(this.ids);
    }
    toJSON() {
        return this.map((i) => { var _a; return ((_a = i.toJSON) === null || _a === void 0 ? void 0 : _a.apply(i)) || i; });
    }
    /**
     * Serialize list. Serialized data could be hydrated with GraphandModel.hydrate
     * @returns {Object}
     */
    serialize() {
        return {
            __type: "GraphandModelList",
            __scope: this.model.scope,
            __count: this.count,
            __query: this.query,
            __payload: this.map((i) => { var _a; return ((_a = i.serialize) === null || _a === void 0 ? void 0 : _a.apply(i)) || i; }),
        };
    }
}
exports.default = GraphandModelList;
